import * as React from 'react'
import { Grid } from '@material-ui/core'
import { ELEMENT_BASE_PARAMS, NewLoading as Loading, NotificationType, ElementOptions, ELEMENT_TYPES, Divider, ElementValue, FlowForm, Permissions, Condition, NewButton, NewModal } from 'prace-common-components'
import { $ButtonContainer, $DeleteButton } from './styles'
import { ElementPermission } from 'components/ElementPermission'
import { ConditionEditor } from 'components/ConditionEditor'
import { DeleteElementModal } from 'components/DeleteElementModal'
import { ElementConditions } from './EditFormElement/ElementConditions'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { RootState } from 'store'
import { useAddEffectMutation, useDeleteEffectsMutation, useUpdateElementMutation, useDeleteFlowElementsMutation } from 'store/api/calls/elements'

export const EditForm: React.FC<{form: FlowForm}> = ({ form }) => {
  const dispatch = useAppDispatch()
  const [updateElement] = useUpdateElementMutation()
  const [deleteElement, { isLoading: isDeleting }] = useDeleteFlowElementsMutation()
  const [addEffect] = useAddEffectMutation()
  const [deleteEffects] = useDeleteEffectsMutation()
  const call = useAppSelector((state: RootState) => state.calls.call)
  const optionsGroups = useAppSelector((state: RootState) => state.calls.optionGroups)
  const [openCondition, setOpenCondition] = React.useState<boolean>(false)
  const [openedCondition, setOpenedCondition] = React.useState<number | undefined>()
  const [conditionLoading, setConditionLoading] = React.useState<boolean>(false)
  const [openDelete, setOpenDelete] = React.useState<boolean>(false)

  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout>>()
  React.useEffect(() => () => clearTimeout(debounceTimerRef.current), [form.id])

  const onChangeOption = (name: string, value: ElementValue) => {
    clearTimeout(debounceTimerRef.current)
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const newValues = ELEMENT_BASE_PARAMS.includes(name)
          ? { id: form.id, [name]: value }
          : { id: form.id, config: { ...(form.config || {}), [name]: value }}
        await updateElement(newValues).unwrap()
        dispatch({ type: 'updateForm', payload: { newValues } })
      } catch (err) {
        console.log(err)
      }
    }, 1000)
  }

  const onChangeElementPermission = async (name: string, value: ElementValue) => {
    try {
      const newValues = { id: form.id, permissions: { ...form.permissions, [name]: value } as Permissions}
      await updateElement(newValues).unwrap()
      dispatch({ type: 'updateForm', payload: { newValues } })
      dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: 'Permissions updated' } })
    } catch (err) {
      console.log(err)
    }
  }

  const onConditionOpen = (idx?: number) => {
    setOpenedCondition(idx)
    setOpenCondition(true)
  }

  const onConditionEdit = async (condition: Condition) => {
    try {
      setConditionLoading(true)
      const effects = [...form.effects || []]
      if(openedCondition !== undefined) {
        const conditionIdx = effects.findIndex((effect: Condition) => effect.id == openedCondition)
        effects.splice(conditionIdx, 1, condition) // Remove condition from index and add new condition
        await updateElement({
          id: form.id,
          effects: [condition], //Only affect this condition
        }).unwrap()
        dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: 'Condition updated' } })
      } else {
        const newCondition = await addEffect({
          ...condition, order: effects.length, id: form.id,
        }).unwrap()
        effects.push(newCondition)
        dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: 'Condition added' } })
      }
      dispatch({ type: 'updateForm', payload: { newValues: { id: form.id, effects } } })
      setConditionLoading(false)
      setOpenCondition(false)
      setOpenedCondition(undefined)
    } catch (err) {
      setConditionLoading(false)
      console.log(err)
    }
  }

  const onDeleteCondition = async (conditionId: number) => {
    try {
      await deleteEffects(conditionId).unwrap()
      const effects = (form.effects || []).filter((effect: Condition) => effect.id !== conditionId)
      dispatch({ type: 'updateForm', payload: { newValues: { id: form.id, effects } } })
      dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: 'Condition deleted' } })
    } catch (err) {
      console.log(err)
    }
  }

  const handleDelete = async () => {
    try {
      await deleteElement({ ids: [form.id] }).unwrap()
      dispatch({ type: 'deleteForm', payload: { formId: form.id } })
      dispatch({ type: 'notification', payload: { type: NotificationType.success, msg: 'Form deleted' } })
    } catch (err) {
      console.log(err)
    }
  }

  return (
    <Grid container>
      <Loading loading={isDeleting}/>
      <ElementOptions element={form} onChange={onChangeOption} />
      <Divider />
      <ElementPermission
        permissions={form.permissions}
        type={ELEMENT_TYPES.FORM}
        id={form.id}
        onChange={onChangeElementPermission}
      />
      <Divider />
      <ElementConditions
        conditions={form.effects}
        call={call}
        onEdit={onConditionOpen}
        onDelete={onDeleteCondition}
      />
      <$ButtonContainer item xs={12}>
        <NewButton variant='outlined' onClick={() => onConditionOpen()}>
          Add Condition
        </NewButton>
      </$ButtonContainer>
      <$DeleteButton error onClick={() => setOpenDelete(true)}>Delete Form</$DeleteButton>
      {openDelete &&
        <NewModal noTopMargin open title={`Delete ${form.title}`} onClose={() => setOpenDelete(false)}>
          <DeleteElementModal
            call={call}
            elements={[form]}
            description='Deleting this form will also delete all elements inside.'
            handleDelete={() => {
              setOpenDelete(false)
              return handleDelete()
            }}
            handleCancel={() => setOpenDelete(false)}
          />
        </NewModal>
      }
      {openCondition &&
        <NewModal alternateTitle title='Add Condition' open
          onClose={() => {
            setOpenCondition(false)
            setOpenedCondition(undefined)
          }}>
          <ConditionEditor
            currentCondition={openedCondition !== undefined && form?.effects ?
              (form.effects.find((cond: Condition) => cond.id === openedCondition)) : undefined}
            onSubmit={onConditionEdit}
            flowElement={form}
            optionGroups={optionsGroups}
            loading={conditionLoading}
          />
        </NewModal>
      }
    </Grid>
  )
}
